/**
 * 日ごとの経費(2026-09-10 指示で新設)の読み書き。
 *  - 項目マスタ: settings/expense_items … { items: [{ id, name }] }(並び順は配列の順。画面から手動で入替)
 *  - 日ごとの金額: expense_daily/{date} … { date, values: { 項目ID: 円 } }
 * ※ PL の経費は現時点では「月次設定」の月額を日割りしたもの。この画面の値はまだ PL に反映していない(P-22)。
 */
import { collection, doc, documentId, getDoc, getDocs, orderBy, query, serverTimestamp, setDoc, where, writeBatch } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { writeAuditLog } from '@core/lib/auditLog';
import type { DateStr } from '@core/lib/types';

export type ExpenseItem = { id: string; name: string };
/** 日付 → 項目ID → 金額(円) */
export type ExpenseDaily = Map<DateStr, Record<string, number>>;

export function newExpenseItemId(): string {
  const rnd = Math.random().toString(36).slice(2, 8);
  return `ex_${Date.now().toString(36)}_${rnd}`;
}

export async function fetchExpenseItems(): Promise<ExpenseItem[]> {
  const snap = await getDoc(doc(getDb(), 'settings', 'expense_items'));
  if (!snap.exists()) return [];
  const items = (snap.data() as { items?: ExpenseItem[] }).items ?? [];
  return items.filter((i) => i && typeof i.id === 'string').map((i) => ({ id: i.id, name: String(i.name ?? '') }));
}

export async function fetchExpenseDaily(from: DateStr, to: DateStr): Promise<ExpenseDaily> {
  const snap = await getDocs(query(collection(getDb(), 'expense_daily'), where(documentId(), '>=', from), where(documentId(), '<=', to), orderBy(documentId())));
  const out: ExpenseDaily = new Map();
  for (const d of snap.docs) {
    const v = (d.data() as { values?: Record<string, number> }).values ?? {};
    out.set(d.id as DateStr, v);
  }
  return out;
}

/** 項目マスタと、変更のあった日の金額をまとめて保存する(1バッチ) */
export async function saveExpenses(args: { items: ExpenseItem[]; changed: { date: DateStr; values: Record<string, number> }[]; userId: string }): Promise<void> {
  const { items, changed, userId } = args;
  for (const it of items) if (!it.name.trim()) throw new Error('項目名を入れてください(空の項目は保存できません)');
  for (const c of changed) {
    for (const [k, v] of Object.entries(c.values)) {
      // マイナス(利益の戻しなど。現行シートの「ｼｽﾃﾑ料利益(ﾎﾜｲﾄ)」)も入れられる
      if (!Number.isInteger(v)) throw new Error(`${c.date.replaceAll('-', '/')} の「${items.find((i) => i.id === k)?.name ?? k}」は整数(円)で入力してください`);
    }
  }
  const db = getDb();
  const batch = writeBatch(db);
  batch.set(doc(db, 'settings', 'expense_items'), { items: items.map((i) => ({ id: i.id, name: i.name.trim() })), updated_by: userId, updated_at: serverTimestamp() });
  for (const c of changed) {
    batch.set(doc(db, 'expense_daily', c.date), { date: c.date, values: c.values, updated_by: userId, updated_at: serverTimestamp() });
  }
  await batch.commit();
  await writeAuditLog({
    targetType: 'import',
    targetId: 'expense_daily',
    action: 'expense-update',
    by: userId,
    changes: [`経費の項目 ${items.length}件 ／ ${changed.length}日分を保存`, ...changed.map((c) => `${c.date}: ${Object.values(c.values).reduce((s, x) => s + x, 0).toLocaleString('ja-JP')}円`)],
  });
}

/** 1日だけ保存する(使い回し用) */
export async function saveExpenseDay(date: DateStr, values: Record<string, number>, userId: string): Promise<void> {
  await setDoc(doc(getDb(), 'expense_daily', date), { date, values, updated_by: userId, updated_at: serverTimestamp() });
}
